/**
 * Supported theme variants that can be applied directly to the UI.
 */
export type ThemeMode = 'light' | 'dark';

/**
 * User theme preference, including delegating the choice to the operating system.
 */
export type ThemePreference = ThemeMode | 'system';

/**
 * Optional configuration used when registering the theming feature.
 */
export interface ThemeOptions {
  /**
   * Initial theme preference used when no persisted value is available.
   *
   * @default 'light'
   */
  defaultPreference?: ThemePreference;

  /**
   * Local storage key used to persist the selected preference.
   *
   * @default 'kros.theme'
   */
  storageKey?: string;

  /**
   * Persists the selected preference to local storage.
   *
   * @default true
   */
  persist?: boolean;

  /**
   * Keeps the resolved theme in sync with the system color scheme when the
   * current preference is set to `'system'`.
   *
   * @default true
   */
  syncWithSystem?: boolean;

  /**
   * Name of the attribute written to the document root with the resolved theme.
   *
   * @default 'data-theme'
   */
  attributeName?: string;

  /**
   * Updates the CSS `color-scheme` property on the document root.
   *
   * @default true
   */
  useColorScheme?: boolean;

  /**
   * Callback invoked after the resolved theme is applied to the document.
   */
  onThemeChange?: (theme: ThemeMode) => void;
}

/**
 * Fully resolved theming configuration with defaults applied.
 */
export interface ResolvedThemeOptions {
  /**
   * Initial theme preference used when no persisted value is available.
   */
  defaultPreference: ThemePreference;

  /**
   * Local storage key used to persist the selected preference.
   */
  storageKey: string;

  /**
   * Persists the selected preference to local storage.
   */
  persist: boolean;

  /**
   * Keeps the resolved theme in sync with the system color scheme.
   */
  syncWithSystem: boolean;

  /**
   * Name of the attribute written to the document root with the resolved theme.
   */
  attributeName: string;

  /**
   * Updates the CSS `color-scheme` property on the document root.
   */
  useColorScheme: boolean;

  /**
   * Callback invoked after the resolved theme is applied to the document.
   */
  onThemeChange: ((theme: ThemeMode) => void) | null;
}

/**
 * Checks whether a value is a supported concrete theme mode.
 */
export function isThemeMode(value: unknown): value is ThemeMode {
  return value === 'light' || value === 'dark';
}

/**
 * Checks whether a value is a valid theme preference, including `'system'`.
 */
export function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'system' || isThemeMode(value);
}
